export const METRIC_LABELS = {
  annotationsSubmitted: "Annotations submitted",
  candidatesGenerated: "Candidates generated",
  candidatesApproved: "Candidates approved",
  candidatesRejected: "Candidates rejected",
};

export const ALL_METRIC_KEYS = Object.keys(METRIC_LABELS);

export const DEFAULT_REPORT_FILTERS = {
  dimension: "institution",
  metrics: [...ALL_METRIC_KEYS],
};

export function toPeriodKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function parsePeriodKey(periodKey) {
  const match = String(periodKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, 1);
}

export function defaultPeriodRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth() - 5, 1);
  return {
    startPeriodKey: toPeriodKey(start),
    endPeriodKey: toPeriodKey(end),
  };
}

function normalizeMetrics(metrics) {
  const selected = Array.isArray(metrics) ? metrics : DEFAULT_REPORT_FILTERS.metrics;
  return selected.filter((key) => ALL_METRIC_KEYS.includes(key));
}

function institutionMetrics(report) {
  return report?.metrics || {};
}

function sumByTciMetrics(report, metricKey) {
  const byTci = report?.byTci || {};
  return Object.values(byTci).reduce(
    (sum, row) => sum + Number(row?.[metricKey] || 0),
    0,
  );
}

function metricsForRow(report, dimension, tciId) {
  if (dimension === "tci" && tciId) {
    return report?.byTci?.[tciId] || {};
  }
  return institutionMetrics(report);
}

/**
 * Build flat table rows for the report builder and PDF export.
 */
export function buildReportTableRows(
  reports = [],
  { dimension = "institution", metrics = ALL_METRIC_KEYS, tciNameById = {} } = {},
) {
  const metricKeys = normalizeMetrics(metrics);
  const rows = [];

  reports.forEach((report) => {
    const periodKey = report.periodKey || "";
    if (dimension === "institution") {
      const values = institutionMetrics(report);
      rows.push({
        periodKey,
        tciId: "",
        tciName: "",
        metrics: Object.fromEntries(
          metricKeys.map((key) => [key, Number(values[key] || 0)]),
        ),
      });
      return;
    }

    const byTci = report.byTci || {};
    const tciIds = Object.keys(byTci);
    if (!tciIds.length) {
      const values = institutionMetrics(report);
      rows.push({
        periodKey,
        tciId: "",
        tciName: "",
        metrics: Object.fromEntries(
          metricKeys.map((key) => [key, Number(values[key] || 0)]),
        ),
      });
      return;
    }

    tciIds.forEach((tciId) => {
      const values = metricsForRow(report, "tci", tciId);
      rows.push({
        periodKey,
        tciId,
        tciName: tciNameById[tciId] || tciId,
        metrics: Object.fromEntries(
          metricKeys.map((key) => [key, Number(values[key] || 0)]),
        ),
      });
    });
  });

  return rows;
}

/**
 * Build chart labels and datasets for Chart.js.
 */
export function buildReportChartData(
  reports = [],
  { dimension = "institution", metrics = ALL_METRIC_KEYS } = {},
) {
  const metricKeys = normalizeMetrics(metrics);
  const sorted = [...reports].sort((a, b) =>
    String(a.periodKey || "").localeCompare(String(b.periodKey || "")),
  );
  const labels = sorted.map((report) => report.periodKey || "");

  const datasets = metricKeys.map((metricKey) => ({
    label: METRIC_LABELS[metricKey] || metricKey,
    data: sorted.map((report) => {
      if (dimension === "institution") {
        return Number(institutionMetrics(report)[metricKey] || 0);
      }
      return sumByTciMetrics(report, metricKey);
    }),
  }));

  return { labels, datasets };
}
